'use client';

import { c as _c } from "react/compiler-runtime";
import { LayoutContainer, Section, TextPassage } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { usePathname } from 'next/navigation';
import type { FC } from 'react';
import { ContentfulClick, trackTile6UpClick, trackTilesClicks } from '@libs/analytics';
import { COLOR_TABLE } from '../../constants';
import type { Tile6UpVariant } from '../../types';
import { getPlpModuleSectionHeading, getTrimmedDisplayTitle } from '../../utils/plpModuleSectionHeading';
import { ColoredTextLink } from '../ColoredTextLink';
import Tile6UpCampaign from './Tile6UpCampaign';
import Tile6UpMultirow from './Tile6UpMultirow';
import Tile6UpPromo from './Tile6UpPromo';
import Tile6UpShoppable from './Tile6UpShoppable';
import Tile6UpTips from './Tile6UpTips';
import Tile6UpVisualNav from './Tile6UpVisualNav';
import type { Tile6UpProps } from './types';
import './Tile6Up.scss';
const ComponentTile6Up: Record<Tile6UpVariant, FC<Tile6UpProps>> = {
  Multirow: Tile6UpMultirow,
  Promo: Tile6UpPromo,
  Shoppable: Tile6UpShoppable,
  VisualNav: Tile6UpVisualNav,
  Tips: Tile6UpTips,
  Campaign: Tile6UpCampaign
};
export default function Tile6Up(t0) {
  const $ = _c(63);
  const {
    variant,
    header,
    backgroundColor,
    textColor,
    linkAction,
    linkActionColor,
    tiles,
    description,
    alignText,
    isH1
  } = t0;
  const Component = ComponentTile6Up[variant];
  const pathname = usePathname();
  if (tiles?.length === 0) {
    return null;
  }
  const isCentered = alignText !== "left";
  let t1;
  if ($[0] !== linkAction) {
    t1 = linkAction || {};
    $[0] = linkAction;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    link,
    actionText
  } = t1;
  let t2;
  if ($[2] !== header) {
    t2 = getTrimmedDisplayTitle(header);
    $[2] = header;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const displayHeader = t2;
  let t3;
  if ($[4] !== header || $[5] !== isH1) {
    t3 = getPlpModuleSectionHeading(header, isH1);
    $[4] = header;
    $[5] = isH1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    title: sectionTitle,
    headingTagName
  } = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ["Multirow", "Promo", "Shoppable", "Tips", "Campaign"];
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const restyledVariants = t4;
  const isRestyled = restyledVariants.includes(variant);
  let t5;
  if ($[8] !== variant) {
    t5 = variant.toLowerCase();
    $[8] = variant;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const variantLowercase = t5;
  const t6 = variant === "Campaign" && !backgroundColor;
  let t7;
  if ($[10] !== t6) {
    t7 = cn({
      "tile6up-container--no-bg": t6
    });
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const paddedContainerClassName = t7;
  const t8 = `tile6up variant-${variantLowercase}`;
  const t9 = !displayHeader && "no-tile-header";
  const t10 = isCentered && "title-center";
  const t11 = isRestyled && "tile6up--restyled";
  let t12;
  if ($[12] !== t10 || $[13] !== t11 || $[14] !== t8 || $[15] !== t9) {
    t12 = cn(t8, t9, t10, t11);
    $[12] = t10;
    $[13] = t11;
    $[14] = t8;
    $[15] = t9;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const componentClassName = t12;
  let t13;
  if ($[17] !== variant) {
    t13 = variant.toLowerCase();
    $[17] = variant;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const t14 = `tile6up-container tile6up-container--${t13} ${paddedContainerClassName}`;
  let t15;
  if ($[19] !== backgroundColor || $[20] !== textColor || $[21] !== variant) {
    t15 = variant === "Campaign" && {
      backgroundColor,
      color: textColor && COLOR_TABLE[textColor]
    };
    $[19] = backgroundColor;
    $[20] = textColor;
    $[21] = variant;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== t15) {
    t16 = {
      ...t15
    };
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  const t17 = !isCentered ? description : "";
  let t18;
  if ($[25] !== isCentered || $[26] !== isRestyled) {
    t18 = isRestyled && {
      align: isCentered ? "center" : undefined,
      titleAfterLocation: "below-description"
    };
    $[25] = isCentered;
    $[26] = isRestyled;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== description || $[29] !== isCentered || $[30] !== textColor || $[31] !== variant) {
    t19 = isCentered && description && <TextPassage style={{
      ...(variant === "Campaign" && {
        color: textColor && COLOR_TABLE[textColor]
      })
    }}>{description}</TextPassage>;
    $[28] = description;
    $[29] = isCentered;
    $[30] = textColor;
    $[31] = variant;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== actionText || $[34] !== header || $[35] !== link || $[36] !== linkActionColor || $[37] !== variant || $[38] !== variantLowercase) {
    t20 = link && actionText && <ContentfulClick eventData={{
      linkText: actionText ?? "",
      linkUrl: link.webUrl ?? "",
      promoTitle: header ?? "",
      promoVariant: `tile 6up - ${variantLowercase}`,
      promoDesc: link.internalTitle ?? "",
      promoType: `tile-6up-${variantLowercase}`
    }}><ColoredTextLink data-testid="tile6up-link-action" href={link?.webUrl} onClick={() => {
        trackTile6UpClick({
          variant,
          name: actionText,
          position: null,
          navigationClick: null
        });
        if (variant === "Multirow") {
          trackTilesClicks({
            variant,
            header: header ?? "",
            title: actionText,
            position: -1,
            row: -1
          });
        }
      }} color={linkActionColor}>{actionText}</ColoredTextLink></ContentfulClick>;
    $[33] = actionText;
    $[34] = header;
    $[35] = link;
    $[36] = linkActionColor;
    $[37] = variant;
    $[38] = variantLowercase;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== t19 || $[41] !== t20) {
    t21 = <>{t19}{t20}</>;
    $[40] = t19;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  const t22 = pathname || "";
  let t23;
  if ($[43] !== Component || $[44] !== header || $[45] !== linkAction || $[46] !== t22 || $[47] !== textColor || $[48] !== tiles || $[49] !== variant) {
    t23 = <Component variant={variant} header={header} linkAction={linkAction} tiles={tiles} pathname={t22} textColor={textColor} />;
    $[43] = Component;
    $[44] = header;
    $[45] = linkAction;
    $[46] = t22;
    $[47] = textColor;
    $[48] = tiles;
    $[49] = variant;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== componentClassName || $[52] !== headingTagName || $[53] !== sectionTitle || $[54] !== t16 || $[55] !== t17 || $[56] !== t18 || $[57] !== t21 || $[58] !== t23) {
    t24 = <Section data-testid="tile6up-test-id" style={t16} title={sectionTitle} headingTagName={headingTagName} description={t17} {...t18} titleAfter={t21} className={componentClassName}>{t23}</Section>;
    $[51] = componentClassName;
    $[52] = headingTagName;
    $[53] = sectionTitle;
    $[54] = t16;
    $[55] = t17;
    $[56] = t18;
    $[57] = t21;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t14 || $[61] !== t24) {
    t25 = <LayoutContainer className={t14}>{t24}</LayoutContainer>;
    $[60] = t14;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  return t25;
}